/**
 * Documentation (F2) — the in-app spec/docs reader (FRONTEND-FOLLOWUP §C).
 *
 * A two-column reading surface over the canonical, implementation, and V3 spec
 * markdown. Titles + grouping come from `lib/docs.ts`; each doc *body* is
 * loaded lazily (its own code-split chunk) via `loadDocBody` on selection, so
 * markdown stays out of the main bundle. Left: a filterable grouped list;
 * right: the selected doc rendered with the shared `<Markdown>` in a
 * `max-w-3xl` reading column. Selection is driven by the `:slug` route param so
 * docs are linkable. All E0 / shadcn-minimal — no card chrome, the single
 * structural fold is the list/pane border.
 */
import * as React from "react";
import { useParams } from "react-router-dom";
import { useGo } from "@refinedev/core";

import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Markdown } from "@/components/app/MarkdownView";
import { Skeleton } from "@/components/ui/skeleton";
import { Icon } from "@/components/ui/icon";
import { PageHeader } from "@/components/layout/PageHeader";
import { EmptyState, ErrorState } from "@/components/ui/states";
import { cn } from "@/lib/utils";
import {
  DOC_GROUPS,
  DOCS,
  getDoc,
  loadDocBody,
  loadScreenMockupHtml,
  standaloneMockupRoutePath,
  type DocEntry,
} from "@/lib/docs";

/**
 * Stable, addressable id for a doc. Both groups carry a `00-README`, so a bare
 * slug is ambiguous; we route by the group-qualified `group:slug` form (parsed
 * back by `getDoc`) to keep every doc independently linkable.
 */
function docKey(doc: DocEntry): string {
  return `${doc.group}:${doc.slug}`;
}

/** One clickable doc row in the left list. */
function DocRow({ doc, active, onSelect }: {
  doc: DocEntry;
  active: boolean;
  onSelect: (key: string) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onSelect(docKey(doc))}
      aria-current={active ? "page" : undefined}
      className={cn(
        "flex w-full items-center gap-2 rounded-md px-3 py-1.5 text-left text-sm transition-colors",
        "hover:bg-accent hover:text-foreground",
        active ? "bg-accent font-medium text-foreground" : "text-muted-foreground",
      )}
    >
      <span className="truncate">{doc.title}</span>
    </button>
  );
}

/** The left list: a filter input + grouped documentation sections. */
function DocList({ activeKey, onSelect }: {
  activeKey: string;
  onSelect: (key: string) => void;
}) {
  const [filter, setFilter] = React.useState("");
  const needle = filter.trim().toLowerCase();

  const groups = React.useMemo(
    () =>
      DOC_GROUPS.map((g) => ({
        group: g.group,
        docs: needle
          ? g.docs.filter((d) =>
              [d.title, d.group, d.slug].some((value) => value.toLowerCase().includes(needle)),
            )
          : g.docs,
      })).filter((g) => g.docs.length > 0),
    [needle],
  );

  return (
    <div className="flex w-full flex-col gap-3 md:h-[calc(100vh-9rem)]">
      <div className="relative">
        <Icon name="search" size={16} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" aria-hidden />
        <Input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter docs…"
          className="pl-8"
          aria-label="Filter documentation"
        />
      </div>
      <ScrollArea className="min-h-0 flex-1">
        <nav className="flex flex-col gap-4 pb-6 pr-2" aria-label="Documentation">
          {groups.map((g) => (
            <div key={g.group} className="flex flex-col gap-0.5">
              <div className="flex items-center gap-2 px-3 pb-1 pt-1">
                <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  {g.group}
                </span>
                <Badge variant="secondary">{g.docs.length}</Badge>
              </div>
              {g.docs.map((doc) => (
                <DocRow key={docKey(doc)} doc={doc} active={docKey(doc) === activeKey} onSelect={onSelect} />
              ))}
            </div>
          ))}
          {groups.length === 0 ? (
            <p className="px-3 py-6 text-sm text-muted-foreground">
              No documents match “{filter}”.
            </p>
          ) : null}
        </nav>
      </ScrollArea>
    </div>
  );
}

/** Loading skeleton for the reading pane while a doc body is fetched. */
function DocBodySkeleton() {
  return (
    <div className="space-y-3" aria-hidden="true">
      <Skeleton className="h-4 w-11/12" />
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-10/12" />
      <Skeleton className="h-4 w-9/12" />
      <Skeleton className="mt-6 h-4 w-full" />
      <Skeleton className="h-4 w-11/12" />
      <Skeleton className="h-4 w-8/12" />
    </div>
  );
}

/**
 * Lazily load the active doc's markdown body (its own code-split chunk). Resets
 * on doc change; ignores stale resolutions so a fast doc switch never flashes
 * the previous body.
 */
function useDocBody(doc: DocEntry | undefined): {
  body: string | null;
  loading: boolean;
  error: boolean;
} {
  const [state, setState] = React.useState<{
    body: string | null;
    loading: boolean;
    error: boolean;
  }>({ body: null, loading: Boolean(doc), error: false });

  React.useEffect(() => {
    if (!doc || doc.assetKind === "html-mockup") {
      setState({ body: null, loading: false, error: false });
      return;
    }
    let active = true;
    setState({ body: null, loading: true, error: false });
    loadDocBody(doc)
      .then((body) => {
        if (active) setState({ body, loading: false, error: body === null });
      })
      .catch(() => {
        if (active) setState({ body: null, loading: false, error: true });
      });
    return () => {
      active = false;
    };
  }, [doc]);

  return state;
}

function useScreenMockupHtml(doc: DocEntry, standalone: boolean): {
  html: string | null;
  loading: boolean;
  error: boolean;
} {
  const [state, setState] = React.useState<{
    html: string | null;
    loading: boolean;
    error: boolean;
  }>({ html: null, loading: doc.assetKind === "html-mockup", error: false });

  React.useEffect(() => {
    if (doc.assetKind !== "html-mockup") {
      setState({ html: null, loading: false, error: false });
      return;
    }
    let active = true;
    setState({ html: null, loading: true, error: false });
    loadScreenMockupHtml(doc, standalone ? "standalone" : "docs")
      .then((html) => {
        if (active) setState({ html, loading: false, error: html === null });
      })
      .catch(() => {
        if (active) setState({ html: null, loading: false, error: true });
      });
    return () => {
      active = false;
    };
  }, [doc, standalone]);

  return state;
}

function ScreenMockupFrame({ doc }: { doc: DocEntry }) {
  const { html, loading, error } = useScreenMockupHtml(doc, false);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3 rounded-md border border-border bg-muted/30 px-3 py-2">
        <span className="text-sm text-muted-foreground">
          Embedded HTML mockup from `specs/v3/screens/`.
        </span>
        <Button asChild variant="outline" size="sm">
          <a href={standaloneMockupRoutePath(doc)} target="_blank" rel="noreferrer noopener">
            Open mockup
          </a>
        </Button>
      </div>
      {loading ? (
        <Skeleton className="h-[78vh] w-full rounded-md" />
      ) : error ? (
        <ErrorState message="This mockup could not be loaded." />
      ) : (
        <iframe
          title={doc.title}
          srcDoc={html ?? ""}
          className="h-[78vh] w-full rounded-md border border-border bg-background"
        />
      )}
    </div>
  );
}

function StandaloneScreenMockup({ doc }: { doc: DocEntry }) {
  const { html, loading, error } = useScreenMockupHtml(doc, true);

  if (loading) return <Skeleton className="h-[100dvh] w-full rounded-none" />;
  if (error) {
    return (
      <div className="flex min-h-[100dvh] items-center justify-center bg-background p-6">
        <ErrorState message="This mockup could not be loaded." />
      </div>
    );
  }
  return (
    <iframe
      title={doc.title}
      srcDoc={html ?? ""}
      className="block h-[100dvh] w-full border-0 bg-background"
    />
  );
}

export function ScreenMockupStandalone() {
  const { slug } = useParams<{ slug: string }>();
  const activeDoc = React.useMemo(
    () => (slug ? getDoc(`V3 Screen Mockups:${slug}`) : undefined),
    [slug],
  );

  if (!activeDoc || activeDoc.assetKind !== "html-mockup") {
    return (
      <div className="flex min-h-[100dvh] items-center justify-center bg-background p-6">
        <ErrorState message="This mockup could not be loaded." />
      </div>
    );
  }

  return <StandaloneScreenMockup doc={activeDoc} />;
}

/**
 * The Documentation reader. Index route (no slug) defaults to the first
 * canonical doc; `:slug` selects a specific doc.
 */
export function Docs() {
  const { slug } = useParams<{ slug: string }>();
  const go = useGo();

  const activeDoc = React.useMemo(() => getDoc(slug ?? "") ?? DOCS[0], [slug]);

  const { body, loading, error } = useDocBody(activeDoc);

  const onSelect = React.useCallback(
    (next: string) => {
      go({ to: `/docs/${encodeURIComponent(next)}`, type: "push" });
    },
    [go],
  );

  if (DOCS.length === 0) {
    return (
      <div className="space-y-6">
        <PageHeader title="Documentation" subtitle="Canonical, implementation, and V3 specs — the single source of truth." icon="docs" />
        <EmptyState icon="docs" title="No documentation bundled" hint="This build was published without the spec markdown." />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Documentation"
        subtitle="Canonical, implementation, and V3 specs — the single source of truth."
        icon="docs"
        actions={<Badge variant="outline">{DOCS.length} docs</Badge>}
      />

      {/*
        Scroll model (§9): the page's own `<main overflow-y-auto>` is the ONE
        scroll container. The left doc-list keeps its bounded `ScrollArea`
        (capped to the viewport so the nav doesn't grow the page), while the
        reading pane is a plain column that flows into the page scroll — NO
        nested article `ScrollArea`. That removes the double-scroll viewport
        that produced the stray scrollbar/thumb artifact at the bottom.
        `min-h-0`/`min-w-0` let the flex children shrink instead of clipping the
        last block.
      */}
      <div className="flex flex-col gap-6 md:flex-row md:items-start">
        <div className="md:sticky md:top-6 md:max-h-[calc(100vh-9rem)] md:w-72 md:shrink-0">
          <DocList activeKey={activeDoc ? docKey(activeDoc) : ""} onSelect={onSelect} />
        </div>

        {/* Reading pane: flows into the page scroll; left border is the fold. */}
        <article className="min-w-0 flex-1 md:border-l md:border-border md:pl-6">
          <div className="max-w-3xl">
            {activeDoc ? (
              <>
                <header className="mb-6 space-y-1">
                  <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    {activeDoc.group}
                  </span>
                  <h2 className="text-2xl font-semibold tracking-tight">{activeDoc.title}</h2>
                </header>
                {activeDoc.assetKind === "html-mockup" ? (
                  <ScreenMockupFrame doc={activeDoc} />
                ) : loading ? (
                  <DocBodySkeleton />
                ) : error ? (
                  <ErrorState message="This document could not be loaded." />
                ) : (
                  <Markdown source={body} />
                )}
              </>
            ) : null}
          </div>
        </article>
      </div>
    </div>
  );
}

export default Docs;
